import {DragAndDrop, type OnDropArgs} from '@github-ui/drag-and-drop'
import type {RepoIssuesDashboardPinnedIssues} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import {useContainerBreakpoint} from '@github-ui/use-container-breakpoint'
import {type RefObject, useCallback, useMemo, useRef, useState} from 'react'

import {usePinnedIssues} from '../../hooks/use-pinned-issues'
import {PinnedIssueCard} from './PinnedIssueCard'
import styles from './PinnedIssuesStrip.module.css'

type PinnedIssuesStripProps = {
  owner: string
  repo: string
  pinnedIssues: RepoIssuesDashboardPinnedIssues | undefined
  fallbackFocusRef: RefObject<HTMLInputElement | null>
}

export function PinnedIssuesStrip({owner, repo, pinnedIssues, fallbackFocusRef}: PinnedIssuesStripProps) {
  const {issues, viewerCanPinIssues, reorder, unpin} = usePinnedIssues(owner, repo, pinnedIssues)
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null)
  const breakpoint = useContainerBreakpoint(scrollContainer)
  const optionsButtonsRef = useRef(new Map<string, HTMLButtonElement>())

  const items = useMemo(() => issues.map(issue => ({id: issue.id, title: issue.title})), [issues])
  const issuesById = useMemo(() => new Map(issues.map(issue => [issue.id, issue])), [issues])

  const onDrop = useCallback(
    ({dragMetadata, dropMetadata, isBefore}: OnDropArgs<string>) => {
      if (!dropMetadata || dragMetadata.id === dropMetadata.id) return
      if (!items.some(item => item.id === dragMetadata.id)) return

      const orderedIds = items.reduce<string[]>((next, item) => {
        if (item.id === dragMetadata.id) return next
        if (item.id !== dropMetadata.id) {
          next.push(item.id)
        } else if (isBefore) {
          next.push(dragMetadata.id, item.id)
        } else {
          next.push(item.id, dragMetadata.id)
        }
        return next
      }, [])

      reorder(orderedIds)
    },
    [items, reorder],
  )

  const getUnpinFocusTarget = useCallback(
    (id: string) => {
      const index = issues.findIndex(issue => issue.id === id)
      const adjacentIssue = issues[index + 1] ?? issues[index - 1]
      return (adjacentIssue && optionsButtonsRef.current.get(adjacentIssue.id)) ?? fallbackFocusRef.current
    },
    [fallbackFocusRef, issues],
  )

  const registerOptionsButton = useCallback((id: string, button: HTMLButtonElement | null) => {
    if (button) optionsButtonsRef.current.set(id, button)
    else optionsButtonsRef.current.delete(id)
  }, [])

  if (issues.length < 1) return null

  const onlySinglePinnedIssue = items.length === 1
  const renderItem = (id: string, index: number, isDragOverlay = false) => {
    const issue = issuesById.get(id)
    if (!issue) return null
    return (
      <DragAndDrop.Item
        index={index}
        id={id}
        key={id}
        title={issue.title}
        hideTrigger={!viewerCanPinIssues}
        className={styles.container}
        style={{
          display: viewerCanPinIssues ? 'grid' : undefined,
          alignItems: 'start',
          gridTemplateColumns: onlySinglePinnedIssue ? '0px 1fr' : '24px 1fr',
          gap: 'var(--base-size-12)',
          paddingLeft: !viewerCanPinIssues ? 'var(--base-size-12)' : 'var(--base-size-8)',
        }}
        isDragOverlay={isDragOverlay}
      >
        <PinnedIssueCard
          issue={issue}
          viewerCanPinIssues={viewerCanPinIssues}
          onUnpin={unpin}
          getUnpinFocusTarget={getUnpinFocusTarget}
          registerOptionsButton={registerOptionsButton}
        />
      </DragAndDrop.Item>
    )
  }

  return (
    <div ref={setScrollContainer}>
      <DragAndDrop
        items={items}
        onDrop={onDrop}
        className={styles.area}
        direction={breakpoint(['vertical', 'vertical', 'horizontal'])}
        aria-label="Drag and drop pinned issues list."
        renderOverlay={(item, index) => renderItem(item.id, index, true)}
      >
        {items.map((item, index) => renderItem(item.id, index))}
      </DragAndDrop>
    </div>
  )
}
